import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PermissionKey } from '@lims/permissions';
import { ApiError, session, setSessionLostHandler } from '../api/client.js';
import { authApi, type CurrentUser } from '../api/endpoints.js';

/**
 * The signed-in user, their laboratory and their effective permissions.
 *
 * `can()` decides what to RENDER. It is never a security control: the backend
 * re-checks every protected operation independently, and a caller who bypasses
 * the interface entirely is rejected there. Hiding a button is a courtesy to
 * the user, not a defence.
 */

interface AuthState {
  readonly user: CurrentUser | null;
  readonly isLoading: boolean;
  readonly can: (permission: PermissionKey | string) => boolean;
  readonly canAny: (...permissions: (PermissionKey | string)[]) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (!session.accessToken()) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      setUser(await authApi.me());
    } catch (error) {
      // An authentication failure means the session is gone; anything else is
      // a transient problem and must not silently sign the user out.
      if (error instanceof ApiError && error.isAuthFailure) {
        session.clear();
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  // The client calls this when a refresh fails, so an expired or revoked
  // session clears the interface rather than leaving a stale one on screen.
  useEffect(() => {
    setSessionLostHandler(() => setUser(null));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setUser(await authApi.login(email, password));
  }, []);

  const signOut = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(user?.permissions ?? []);
    return {
      user,
      isLoading,
      can: (permission) => permissions.has(permission),
      canAny: (...required) => required.some((key) => permissions.has(key)),
      signIn,
      signOut,
      refreshUser: loadUser,
    };
  }, [user, isLoading, signIn, signOut, loadUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.');
  }
  return context;
}
